import { ref, watch, computed } from 'vue'
import { safeLocalStorage, isClient } from '@/composables/useSSR'

const STORAGE_KEY = 'adminNavCollapseState'

// Group keys for admin navigation
export type AdminNavGroupKey = 'business' | 'users' | 'domains' | 'system'

interface CollapseState {
  business: boolean
  users: boolean
  domains: boolean
  system: boolean
}

// Default: all groups expanded (false = not collapsed)
const defaultState: CollapseState = {
  business: false,
  users: false,
  domains: false,
  system: false
}

// Load from localStorage or use defaults
const loadState = (): CollapseState => {
  const storage = safeLocalStorage()
  if (!storage) return defaultState

  const stored = storage.getItem(STORAGE_KEY)
  if (stored) {
    try {
      return { ...defaultState, ...JSON.parse(stored) }
    } catch {
      return defaultState
    }
  }
  return defaultState
}

// Shared reactive state
const collapseState = ref<CollapseState>(isClient ? loadState() : defaultState)

// Sync to localStorage (client only)
if (isClient) {
  watch(
    collapseState,
    (newValue) => {
      const storage = safeLocalStorage()
      if (storage) {
        storage.setItem(STORAGE_KEY, JSON.stringify(newValue))
      }
    },
    { deep: true }
  )
}

// Listen for storage changes from other tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY && event.newValue) {
      try {
        collapseState.value = { ...defaultState, ...JSON.parse(event.newValue) }
      } catch {
        // Ignore parse errors
      }
    }
  })
}

export function useAdminNavCollapse() {
  const isCollapsed = (groupKey: AdminNavGroupKey): boolean => {
    return collapseState.value[groupKey]
  }

  const toggle = (groupKey: AdminNavGroupKey) => {
    collapseState.value = {
      ...collapseState.value,
      [groupKey]: !collapseState.value[groupKey]
    }
  }

  const expand = (groupKey: AdminNavGroupKey) => {
    if (collapseState.value[groupKey]) {
      collapseState.value = {
        ...collapseState.value,
        [groupKey]: false
      }
    }
  }

  const expandAll = () => {
    collapseState.value = { business: false, users: false, domains: false, system: false }
  }

  const collapseAll = () => {
    collapseState.value = { business: true, users: true, domains: true, system: true }
  }

  return {
    collapseState: computed(() => collapseState.value),
    isCollapsed,
    toggle,
    expand,
    expandAll,
    collapseAll
  }
}
